import { ComponentPropsWithoutRef } from 'react';

import { usePaymentMethodsQuery } from '@/api/getters';
import CardIcon from '@/assets/card.svg?react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/Tooltip';
import CheckoutSummarySection from '@/features/v2/hh/components/CheckoutSummarySection';
import cn from '@/utils/cn';

import { useLingui } from '@lingui/react';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';

interface PaymentMethodsProps
  extends Omit<
    ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>,
    'onValueChange'
  > {
  onValueChange?: (value: string) => void;
}

const PaymentMethods = ({
  className,
  onValueChange,
  value,
  ...props
}: PaymentMethodsProps) => {
  const { i18n } = useLingui();
  const { data: paymentMethods = [] } = usePaymentMethodsQuery();

  return (
    <CheckoutSummarySection
      icon={<CardIcon width={16} className="text-primary" />}
      title={i18n.t('Metody płatności')}
    >
      <RadioGroupPrimitive.Root
        value={value}
        onValueChange={onValueChange}
        className={cn(
          'flex w-full flex-wrap justify-between gap-3 rounded-md border border-gray-500 bg-white p-4 md:gap-4 lg:gap-[15px] lg:p-6',
          className
        )}
        {...props}
      >
        {paymentMethods.map((method) => {
          const checked = value === method.name;
          return (
            <TooltipProvider key={method.id} delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  {/* A real (non `display: contents`) box: Radix measures the
                      tooltip's anchor position from this span, and a `contents`
                      box has no geometry of its own (getBoundingClientRect is
                      all zeros), which pins the tooltip to the viewport origin.
                      `inline-flex` hugs the item's size so the wrapper stays
                      invisible, while keeping the RadioGroup item's own
                      `data-state` (checked/unchecked) intact — asChild on both
                      primitives directly would let the Tooltip's data-state
                      overwrite it. */}
                  <span className="inline-flex">
                    <RadioGroupPrimitive.Item
                      value={method.name}
                      aria-label={method.name}
                      className={cn(
                        'flex size-12 shrink-0 items-center rounded border transition-colors duration-200 ease-in-out',
                        'focus-visible:outline-none',
                        checked
                          ? 'border-green-500'
                          : 'border-white hover:border-green-500'
                      )}
                    >
                      <img
                        src={method.imgUrl}
                        alt={method.name}
                        className="size-full object-contain"
                      />
                    </RadioGroupPrimitive.Item>
                  </span>
                </TooltipTrigger>
                <TooltipContent className="px-2 py-1 text-xs">
                  <p>{method.name}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        })}
      </RadioGroupPrimitive.Root>
    </CheckoutSummarySection>
  );
};

export default PaymentMethods;
